import React, { useState, useEffect } from "react";
import { api } from "../lib/api";
import { MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * WASendButton — every WhatsApp outreach button in the CRM goes through this.
 *
 * Flow: click → backend checks if this phone was messaged in the last 24h.
 *   • Not sent recently → logs the send, opens wa.me.
 *   • Already sent      → caution dialog ("you sent this person a WhatsApp
 *     earlier — send one more?"). OK → force-log + open; Cancel → nothing.
 * After sending, the button shows "Sent ✓ <time>" for 24 hours, then resets.
 */
export default function WASendButton({ phone, customerName = "", context = "general", waHref, className = "btn-gold text-xs", label = "WhatsApp" }) {
  const [sentAt, setSentAt] = useState(null);
  const [confirm, setConfirm] = useState(null); // {last_sent_at, last_sent_by}
  const [busy, setBusy] = useState(false);

  // Restore sent state from the shared 24h log
  useEffect(() => {
    let mounted = true;
    api.get("/api/crm/wa-send/recent").then((r) => {
      if (!mounted) return;
      const mine = (r.data || []).find((s) => s.phone === phone);
      if (mine) setSentAt(mine.at);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [phone]);

  const openWA = () => window.open(waHref, "_blank", "noopener");

  const doSend = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/crm/wa-send", { phone, customer_name: customerName, context, force });
      if (data.duplicate) {
        setConfirm(data); // show caution
      } else {
        setSentAt(new Date().toISOString());
        setConfirm(null);
        openWA();
      }
    } catch (e) {
      toast.error("Could not log the send");
      openWA(); // never block the actual message
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <button onClick={() => doSend(false)} disabled={busy} className={className} data-testid={`wa-send-${phone}`}>
          <MessageCircle size={12}/> <span>{label}</span>
        </button>
        {sentAt && (
          <span className="inline-flex items-center gap-1 text-[10px] text-green-700" data-testid={`wa-sent-${phone}`}>
            <Check size={11}/> Sent {new Date(sentAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </div>

      {confirm && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={() => setConfirm(null)}>
          <div className="bg-white border border-line p-6 max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-2xl text-maroon">Already messaged</div>
            <p className="text-sm text-ash mt-2">
              A WhatsApp message was already sent to {customerName || "this person"} ({phone})
              {confirm.last_sent_by && <> by <b>{confirm.last_sent_by}</b></>} at {new Date(confirm.last_sent_at).toLocaleString()}.
              Do you want to send one more?
            </p>
            <div className="mt-4 flex gap-3 justify-end">
              <button onClick={() => setConfirm(null)} className="btn-outline text-xs" data-testid="wa-dup-cancel">Cancel</button>
              <button onClick={() => { setSentAt(new Date().toISOString()); setConfirm(null); api.post("/api/crm/wa-send", { phone, customer_name: customerName, context, force: true }).catch(() => {}); openWA(); }} className="btn-maroon text-xs" data-testid="wa-dup-resend">Yes, send again</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
